import { createTheme } from "@material-ui/core/styles";

import AsyncButton from "./components/AsyncButton";
import DefaultAvatar from "./components/Avatar/DefaultAvatar";
import Badge from "./components/Badge";
import Button from "./components/Button";
import ButtonGroup, { GroupButton } from "./components/ButtonGroup";
import CardActions from "./components/Card/CardActions";
import type { CardHeaderProps } from "./components/Card/CardHeader";
import CardHeader from "./components/Card/CardHeader";
import CardLoading from "./components/Card/CardLoading";
import type { CardTab } from "./components/Card/CardTabs";
import CardTabs from "./components/Card/CardTabs";
import ListCard from "./components/Card/ListCard";
import Checkbox from "./components/Checkbox";
import type { ChipProps } from "./components/Chip";
import Chip from "./components/Chip";
import ContextContainer from "./components/ContextContainer";
import ContextMenu from "./components/ContextMenu";
import ButtonDropdown from "./components/dropdown/ButtonDropdown";
import CheckboxDropdown from "./components/dropdown/CheckboxDropdown";
import type { DropdownOption } from "./components/dropdown/DropdownOption";
import type { DynamicInfiniteListProps, ListRecordResponse } from "./components/DynamicInfiniteList";
import DynamicInfiniteList from "./components/DynamicInfiniteList";
import type { EmptyListProps } from "./components/EmptyList";
import EmptyList from "./components/EmptyList";
import FormInput from "./components/FormInput";
import type { InfiniteGroup } from "./components/GroupedInfiniteList";
import GroupedInfiniteList from "./components/GroupedInfiniteList";
import type { Group, GroupedListProps } from "./components/GroupedList";
import GroupedList from "./components/GroupedList";
import IconButton from "./components/IconButton";
import IconButtonGroup, { IconToggleButton } from "./components/IconButtonGroup";
import IconFilterGroup, { IconFilterButton } from "./components/IconFilterGroup";
import type { IframeSkeletonProps } from "./components/IframeSkeleton";
import IframeSkeleton from "./components/IframeSkeleton";
import type { InfiniteListProps } from "./components/InfiniteList/InfiniteList";
import InfiniteList from "./components/InfiniteList/InfiniteList";
import InputRounded from "./components/InputRounded";
import type { MapUserIconProps } from "./components/MapUserIcon";
import MapUserIcon from "./components/MapUserIcon";
import type { MarkerLabelProps } from "./components/MarkerLabel";
import MarkerLabel from "./components/MarkerLabel";
import type { MobileNavHeaderProps } from "./components/MobileNavHeader";
import MobileNavHeader from "./components/MobileNavHeader";
import MenuListItem from "./components/nav/MenuListItem";
import { NavMenu } from "./components/nav/NavMenu";
import LinkTab, { LoadingTab, TabPanel } from "./components/nav/Tab";
import type { MenuItem } from "./components/nav/types";
import OptionActions from "./components/OptionCard";
import OptionPanel, { Side } from "./components/OptionPanel";
import EventOverlay from "./components/overlay/EventOverlay";
import { EventColour } from "./components/overlay/types";
import PageToggle from "./components/page-toggle/PageToggle";
import type { QuickFieldFilter, QuickFilter, QuickFilterProps, QuickIconFilter } from "./components/QuickFilters";
import QuickFilters from "./components/QuickFilters";
import { ResultsBar } from "./components/ResultsBar/ResultsBar";
import Search from "./components/Search";
import SettingsDialog from "./components/SettingsDialog";
import type { TabProps, TabsProps } from "./components/tabs";
import { Tab, Tabs } from "./components/tabs";
import Toggle from "./components/Toggle";
import Tooltip from "./components/Tooltip";
import type { ContextMenuItem, Coord } from "./components/types";
import useEventColour from "./hooks/useEventColour";
import { Logo } from "./Logo";
import type { MapContext, MapEvents, MapMachineSchema } from "./statemachines/MapMachine";
import mapMachine from "./statemachines/MapMachine";
import type { CoretexPalette, CoretexTheme, CoretexTypography } from "./theme";
import theme from "./theme/coretex-theme";
import darkTheme from "./theme/coretex-theme-dark";
import EditMarker from "./tracking/EditMarker";
import { InspectorPanel } from "./tracking/InspectorPanel";
import Message from "./tracking/Message";
import type { NotificationItem, NotificationStackProps } from "./tracking/notifications";
import { NotificationStack, useNotification } from "./tracking/notifications";
import RangedTimeline from "./tracking/RangeTimeline";
import SearchSlim from "./tracking/SearchSlim";
import SearchTool from "./tracking/SearchTool";
import type { ButtonCommand, SearchToolbarProps } from "./tracking/SearchToolbar";
import SearchToolbar from "./tracking/SearchToolbar";
import SectionExpand from "./tracking/SectionExpand";
import type { DeprecatedTabProps } from "./tracking/tabs/DeprecatedTab";
import DeprecatedTab from "./tracking/tabs/DeprecatedTab";
import type { DeprecatedTabsProps } from "./tracking/tabs/DeprecatedTabs";
import DeprecatedTabs from "./tracking/tabs/DeprecatedTabs";
import TrackingSettings from "./tracking/TrackingSettings";
import type { AlertItem, SearchOption } from "./tracking/types";
import type { VehicleAlertsProps } from "./tracking/VehicleAlerts";
import VehicleAlerts from "./tracking/VehicleAlerts";

const materialTheme = createTheme(theme);
const materialThemeDark = createTheme(darkTheme);

export {
  AsyncButton,
  Badge,
  Button,
  ButtonDropdown,
  ButtonGroup,
  CardActions,
  CardHeader,
  CardLoading,
  CardTabs,
  Checkbox,
  CheckboxDropdown,
  Chip,
  ContextContainer,
  ContextMenu,
  DefaultAvatar,
  DeprecatedTab,
  DeprecatedTabs,
  DynamicInfiniteList,
  EditMarker,
  EmptyList,
  EventColour,
  EventOverlay,
  FormInput,
  GroupButton,
  GroupedInfiniteList,
  GroupedList,
  IconButton,
  IconButtonGroup,
  IconFilterButton,
  IconFilterGroup,
  IconToggleButton,
  IframeSkeleton,
  InfiniteList,
  InputRounded,
  InspectorPanel,
  LinkTab,
  ListCard,
  LoadingTab,
  Logo,
  mapMachine,
  MapUserIcon,
  MarkerLabel,
  materialTheme,
  materialThemeDark,
  MenuListItem,
  Message,
  MobileNavHeader,
  NavMenu,
  NotificationStack,
  OptionActions,
  OptionPanel,
  PageToggle,
  QuickFilters,
  RangedTimeline,
  ResultsBar,
  Search,
  SearchSlim,
  SearchTool,
  SearchToolbar,
  SectionExpand,
  SettingsDialog,
  Side,
  Tab,
  TabPanel,
  Tabs,
  Toggle,
  Tooltip,
  TrackingSettings,
  useEventColour,
  useNotification,
  VehicleAlerts,
};

export type {
  AlertItem,
  ButtonCommand,
  CardHeaderProps,
  CardTab,
  ChipProps,
  ContextMenuItem,
  Coord,
  CoretexPalette,
  CoretexTheme,
  CoretexTypography,
  DeprecatedTabProps,
  DeprecatedTabsProps,
  DropdownOption,
  DynamicInfiniteListProps,
  EmptyListProps,
  Group,
  GroupedListProps,
  IframeSkeletonProps,
  InfiniteGroup,
  InfiniteListProps,
  ListRecordResponse,
  MapContext,
  MapEvents,
  MapMachineSchema,
  MapUserIconProps,
  MarkerLabelProps,
  MenuItem,
  MobileNavHeaderProps,
  NotificationItem,
  NotificationStackProps,
  QuickFieldFilter,
  QuickFilter,
  QuickFilterProps,
  QuickIconFilter,
  SearchOption,
  SearchToolbarProps,
  TabProps,
  TabsProps,
  VehicleAlertsProps,
};
